// Canonical in-app paths for the crawlable SEO surfaces.
//
// These exist so interactive cards can carry a real `href` alongside their
// click handler. Googlebot indexes the RENDERED DOM, and an onClick-only <div>
// is invisible to it — without real anchors the app renders with zero links
// and no internal link graph reaches the index.
//
// memeHubPath must match MemeHubHelpers' segment construction on the Rails
// side (spaces -> hyphens, then URL-encoded) or the href and the canonical
// disagree.
function encodeRailsPathSegment(segment: string): string {
    // encodeURIComponent leaves !'()* unescaped, while ERB::Util.url_encode
    // escapes them. Apply the RFC 3986 tightening so client and server paths
    // remain byte-for-byte identical for legacy aliases outside MEME_REGEX.
    return encodeURIComponent(segment).replace(
        /[!'()*]/g,
        char => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
    )
}

export function memeHubPath(text?: string | null): string | undefined {
    const label = (text || '').trim()
    if (!label) return undefined
    return `/meme/${encodeRailsPathSegment(label.split(' ').join('-'))}`
}

export function streamHubPath(streamId?: string | null): string | undefined {
    const id = (streamId || '').trim()
    return id ? `/stream/${id}` : undefined
}

export function questPath(questKey?: string | null): string | undefined {
    const key = (questKey || '').trim()
    return key ? `/quest/${key}` : undefined
}

export function questAnswerPath(
    questHref?: string | null,
    answerId?: string | null,
): string | undefined {
    const base = (questHref || '').trim().replace(/\/+$/, '')
    const id = (answerId || '').trim()
    if (!base) return undefined
    return id ? `${base}/answer/${encodeRailsPathSegment(id)}` : base
}

// In-app paths that must survive rich-text link validation. Tiptap's default
// URI validation is aimed at absolute external URLs, so without this our own
// crawlable hrefs get stripped and the anchors render with no link at all.
const INTERNAL_SEO_PATH =
    /^\/(?:(?:memes|streams)(?:[/?#]|$)|(?:meme|stream|quest)\/[^/?#]+(?:[/?#]|$))/

export function isInternalSeoPath(url?: string | null): boolean {
    return typeof url === 'string' && INTERNAL_SEO_PATH.test(url.trim())
}
